import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { updateUploadZip } from "@/lib/api";

const TIER_LABEL = {
    high_cost: "High-cost market",
    above_avg: "Above-average market",
    average: "Average market",
    lower_cost: "Lower-cost market",
};

/**
 * Compact dialog to retroactively localize an existing report.
 * Same backend endpoint as the post-checkout ZIP prompt.
 */
export default function LocalizeReportDialog({ report, onClose, onApplied }) {
    const open = !!report;
    const [zip, setZip] = useState("");
    const [lookup, setLookup] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            setZip("");
            setLookup(null);
        }
    }, [open]);

    useEffect(() => {
        if (zip.length !== 5) {
            setLookup(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/cost-engine/market-lookup?zip_code=${zip}`,
                );
                if (!res.ok) return;
                const j = await res.json();
                if (!cancelled) setLookup(j);
            } catch {
                if (!cancelled) setLookup(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [zip]);

    const apply = async () => {
        if (!report?.upload_id || zip.length !== 5) return;
        try {
            setSaving(true);
            const r = await updateUploadZip(report.upload_id, zip);
            toast.success(
                r.recomputed
                    ? `Localized to ${r.market_profile_name}.`
                    : `Saved ZIP ${zip}.`,
            );
            onApplied?.(r);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Could not apply ZIP — please try again.",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose?.()}>
            <DialogContent
                className="max-w-md bg-bl-surface border-bl rounded-2xl"
                data-testid="localize-report-dialog"
            >
                <DialogHeader>
                    <div className="bl-eyebrow w-fit flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" /> Localize cost ranges
                    </div>
                    <DialogTitle className="font-display text-2xl text-bl mt-2">
                        What's the property ZIP?
                    </DialogTitle>
                    <DialogDescription className="text-bl-muted leading-relaxed">
                        Adding the ZIP for{" "}
                        <span className="text-bl">
                            {report?.property_address || "this property"}
                        </span>{" "}
                        re-applies our localized cost engine to every issue —
                        labor + material multipliers, contractor minimums, and
                        permit costs for that market. Free and instant — no
                        re-analysis needed.
                    </DialogDescription>
                </DialogHeader>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    <input
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        value={zip}
                        onChange={(e) =>
                            setZip(e.target.value.replace(/\D/g, "").slice(0, 5))
                        }
                        placeholder="ZIP"
                        className="px-3 py-2.5 bg-bl-bg rounded-lg border border-bl text-bl text-base outline-none focus:border-bl-primary font-mono-bl w-28 tracking-widest"
                        data-testid="localize-zip-input"
                        autoFocus
                    />
                    <button
                        type="button"
                        onClick={apply}
                        disabled={zip.length !== 5 || saving}
                        className="bl-btn-primary"
                        data-testid="localize-apply-btn"
                    >
                        {saving ? "Localizing…" : "Localize"}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="bl-btn-ghost text-sm"
                        data-testid="localize-cancel-btn"
                    >
                        Cancel
                    </button>
                </div>
                {lookup?.profile && (
                    <div
                        className="mt-3 text-xs text-bl-muted"
                        data-testid="localize-lookup-result"
                    >
                        <span className="text-bl">{lookup.profile.name}</span>{" "}
                        ·{" "}
                        {TIER_LABEL[lookup.profile.tier] || lookup.profile.tier}
                        {!lookup.known && (
                            <span className="ml-1 italic">
                                (estimated — ZIP not in our local-cost map yet)
                            </span>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
